import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { LogOut, HelpCircle, Calendar, Activity, Trophy, BookOpen, Heart, AlertTriangle } from 'lucide-react';
import { TCSLogo } from '../components/TCSLogo';
import { NavArrows } from '../components/NavArrows';

const FAQS = [
  {
    id: 'missed-training',
    icon: Calendar,
    question: '1. Perdi o treino. O que fazer?',
    answer: (
      <div className="space-y-3 text-white/90 text-sm md:text-base leading-relaxed">
        <p>Você pode executar o treino que falta no outro dia.</p>
        <p>Você também pode executar os treinos específicos de academia ou casa que estão no <strong className="text-[#00F0FF]">Protocolo TCS</strong>, a sua escolha.</p>
        <p>Por fim, você pode seguir a planilha normal com o treino do outro dia e ignorar o não feito.</p>
      </div>
    )
  },
  {
    id: 'sick',
    icon: AlertTriangle,
    question: '2. Estou doente ou me sentindo mal. O que fazer?',
    answer: (
      <div className="space-y-3 text-white/90 text-sm md:text-base leading-relaxed">
        <p className="text-[#FF3366] font-semibold">Sintomas, quaisquer que sejam, você precisa procurar o médico.</p>
        <p>Porém, se seus sintomas forem leves, fique em <strong className="text-[#FFB800]">OFF total por 2 dias</strong> e sinta seu corpo após essas 48 horas.</p>
        <p>Volte em <strong className="text-[#00F0FF]">baixa intensidade</strong>, seguindo as recomendações dos treinos.</p>
        <p>Se mesmo assim estiver mal, <strong className="text-[#FF3366]">pare os treinos e procure seu treinador</strong>.</p>
      </div>
    )
  },
  {
    id: 'competition-week',
    icon: Trophy,
    question: '3. Semana de competição. O que fazer?',
    answer: (
      <div className="space-y-3 text-white/90 text-sm md:text-base leading-relaxed">
        <p>Reduzir a carga total para <strong className="text-[#00F0FF]">50%</strong> e fazer todos os treinos normais que estão na plataforma, sejam eles qual for, com a redução da carga de 50% pra menos.</p>
        <p>Aumentar a ingestão de carboidratos.</p>
        <p>No <strong className="text-[#FFB800]">sábado, OFF total</strong> — não treinar. Caso não treine na sexta, sábado apenas um giro leve de 1:00 em Z1/Z2.</p>
        <div className="bg-[#FF3366]/10 border border-[#FF3366]/40 p-3 mt-3">
          <p className="text-[#FF3366] font-semibold text-xs uppercase tracking-wider mb-1">Atenção</p>
          <p className="text-white">Em hipótese alguma use suplementos diferentes ou coma comidas diferentes.</p>
        </div>
      </div>
    )
  },
  {
    id: 'protocol-updates',
    icon: BookOpen,
    question: '4. O Protocolo TCS é atualizado sempre?',
    answer: (
      <div className="space-y-3 text-white/90 text-sm md:text-base leading-relaxed">
        <p>Use o Protocolo TCS para muitas informações — <strong className="text-[#00F0FF]">leia-o, estude-o, use-o ao seu benefício e encaixe-o à sua rotina</strong>.</p>
        <p>Lembre-se que ele é <strong className="text-[#39FF14]">atualizado infinitamente</strong>. Então baixe, estude e veja como ele se encaixa na sua rotina.</p>
        <p>Tudo está bem detalhado e bem elaborado para você.</p>
      </div>
    )
  },
  {
    id: 'gym',
    icon: Activity,
    question: '5. Posso fazer academia?',
    answer: (
      <div className="space-y-3 text-white/90 text-sm md:text-base leading-relaxed">
        <p>Sim, você deve fazer treino na academia.</p>
        <p>Trabalhe <strong className="text-[#00F0FF]">força, potência, flexibilidade, estabilidade e prevenção</strong> — tudo está detalhado no <strong className="text-[#00F0FF]">Protocolo TCS</strong>. Siga as recomendações e execute exatamente os treinos prescritos.</p>
        <p><strong className="text-[#FFB800]">Frequência:</strong> mínimo 2× por semana e máximo 3× por semana.</p>
        <div className="bg-[#FF3366]/10 border border-[#FF3366]/40 p-3 mt-3">
          <p className="text-[#FF3366] font-semibold text-xs uppercase tracking-wider mb-1">Atenção</p>
          <p className="text-white">Em hipótese alguma treine academia em semana de competição.</p>
        </div>
      </div>
    )
  },
  {
    id: 'monitoring',
    icon: Heart,
    question: '6. Por que é importante responder o questionário diário?',
    answer: (
      <div className="space-y-3 text-white/90 text-sm md:text-base leading-relaxed">
        <p>O questionário diário mede sua <strong className="text-[#00F0FF]">sensação geral, dores musculares, recuperação, saúde mental e FC em repouso</strong>.</p>
        <p>Com ele, o app calcula seu score diário e indica se você está <strong className="text-[#39FF14]">Pronto para Treino</strong>, em <strong className="text-[#FFB800]">Alerta</strong> ou em <strong className="text-[#FF3366]">Extrema Carga</strong>.</p>
        <p>Responder todo dia é a principal ferramenta de prevenção de overtraining e problemas cardíacos.</p>
      </div>
    )
  },
  {
    id: 'ctl-when',
    icon: Activity,
    question: '7. Quando devo registrar meu CTL?',
    answer: (
      <div className="space-y-3 text-white/90 text-sm md:text-base leading-relaxed">
        <p>O CTL (Chronic Training Load) deve ser registrado <strong className="text-[#00F0FF]">1 vez por ciclo mensal</strong>, próximo à sua data de renovação da mensalidade.</p>
        <p>Copie os valores diretamente do <strong>Performance Management Chart</strong> do TrainingPeaks: Fadiga (ATL), Condicionamento (CTL) e Forma (TSB).</p>
        <p>Após registrado, o gráfico mostrará sua evolução mês a mês — subida ou descida — com a seta de tendência ao lado do valor principal.</p>
      </div>
    )
  },
  {
    id: 'support',
    icon: HelpCircle,
    question: '8. Outras dúvidas ou casos específicos?',
    answer: (
      <div className="space-y-3 text-white/90 text-sm md:text-base leading-relaxed">
        <p>Para casos não listados aqui, <strong className="text-[#00F0FF]">entre em contato direto com o treinador Thyago Costa Silva</strong>.</p>
        <p>Sintomas graves, lesões e dúvidas médicas devem ser tratados sempre com profissional da saúde. O app e o treinador auxiliam na performance — a sua saúde é prioridade absoluta.</p>
      </div>
    )
  },
];

const Help = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-xl sticky top-0 z-20">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <NavArrows />
            <TCSLogo size="sm" showTagline={false} />
            <div className="hidden sm:block">
              <h1 className="text-lg md:text-xl font-black uppercase tracking-tight text-[#00F0FF]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Ajuda
              </h1>
              <p className="text-[10px] md:text-xs tracking-[0.2em] uppercase text-[#A1A1AA]">Perguntas Frequentes</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            size="icon"
            variant="outline"
            className="bg-transparent border-[#FF3366] text-[#FF3366] hover:bg-[#FF3366]/10 rounded-none flex-shrink-0"
            data-testid="help-logout-button"
            aria-label="Sair"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 md:py-10 max-w-3xl" data-testid="help-main">
        <div className="mb-6 md:mb-8">
          <div className="flex items-center gap-2 mb-2">
            <HelpCircle className="h-5 w-5 text-[#00F0FF]" />
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA]">Central de Ajuda</p>
          </div>
          <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-white mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Dúvidas Frequentes
          </h2>
          <p className="text-sm md:text-base text-[#A1A1AA] leading-relaxed">
            Respostas rápidas para as situações mais comuns do dia a dia do atleta. Para casos específicos, fale com o treinador <span className="text-white font-semibold">Thyago Costa Silva</span>.
          </p>
        </div>

        <Accordion type="single" collapsible className="space-y-3">
          {FAQS.map((faq) => {
            const Icon = faq.icon;
            return (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="bg-[#121212] border border-white/10 rounded-none px-4 md:px-6"
                data-testid={`help-faq-${faq.id}`}
              >
                <AccordionTrigger className="py-4 md:py-5 hover:no-underline text-left">
                  <div className="flex items-start gap-3 text-left">
                    <Icon className="h-5 w-5 text-[#00F0FF] flex-shrink-0 mt-0.5" />
                    <span className="text-base md:text-lg font-bold text-white pr-2">{faq.question}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-5 pt-1 pl-8">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        <div className="mt-10 md:mt-12 bg-[#00F0FF]/5 border border-[#00F0FF]/30 p-5 md:p-6">
          <div className="flex items-start gap-3">
            <Heart className="h-5 w-5 text-[#00F0FF] flex-shrink-0 mt-0.5" fill="#00F0FF" />
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#00F0FF] mb-1">Lembrete TCS</p>
              <p className="text-sm md:text-base text-white leading-relaxed">
                Sua saúde é prioridade. Monitoramento diário, registro mensal do CTL e comunicação constante com o treinador são o tripé do atleta de alto desempenho.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Help;
